import React, { useState, useEffect } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface TaskImportData {
  type: string;
  title: string;
  description?: string;
  clientId: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  frequency: string;
  dueDate?: string;
  preparerId?: string;
  reviewerId?: string;
  submitterId?: string;
  feeType?: string;
  fixedAmount?: number;
  billableRate?: number;
  estimatedHours?: number;
  tags?: string;
}

interface TaskImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (tasks: TaskImportData[], file?: File) => void;
  isLoading?: boolean;
}

interface ImportResult {
  successful: any[];
  failed: Array<{ task: TaskImportData; error: string }>;
  total: number;
}

interface TaskImportResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: ImportResult | null;
}

export const TaskImportResultsModal: React.FC<TaskImportResultsModalProps> = ({
  isOpen,
  onClose,
  results,
}) => {
  if (!isOpen || !results) return null;

  const successCount = results.successful.length;
  const failureCount = results.failed.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Import Results</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900">Total Records</p>
                  <p className="text-2xl font-bold text-blue-600">{results.total}</p>
                </div>
                <Upload className="w-8 h-8 text-blue-400" />
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-900">Successfully Imported</p>
                  <p className="text-2xl font-bold text-green-600">{successCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-900">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{failureCount}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>
          </div>

          {/* Failed Imports */}
          {failureCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start mb-4">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
                <h3 className="text-lg font-medium text-red-900">Failed Imports ({failureCount})</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-red-200">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Title</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Client ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-900 uppercase">Reason for Failure</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-red-100">
                    {results.failed.map((item, index) => (
                      <tr key={index} className="hover:bg-red-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.task.type}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.task.title}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.task.clientId}</td>
                        <td className="px-4 py-3 text-sm text-red-600">{item.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successCount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-lg font-medium text-green-900">Successfully Imported</h3>
                  <p className="text-sm text-green-700 mt-1">
                    {successCount} task{successCount !== 1 ? 's' : ''} {successCount !== 1 ? 'have' : 'has'} been successfully imported.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const TaskImportModal: React.FC<TaskImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isLoading = false,
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing'>('upload');
  const [parsedData, setParsedData] = useState<TaskImportData[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setParsedData([]);
      setErrors([]);
      setSelectedFile(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isLoading) {
      setStep('importing');
    }
  }, [isLoading]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      parseCSVFile(file);
    }
  };

  const parseCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const parsed = parseCSV(text);
        setParsedData(parsed.data);
        setErrors(parsed.errors);
        setStep('preview');
      } catch (error) {
        setErrors(['Failed to parse CSV file. Please check the format.']);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): { data: TaskImportData[]; errors: string[] } => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
    const data: TaskImportData[] = [];
    const errors: string[] = [];

    // Expected headers mapping (15 columns total)
    const headerMap: Record<string, string> = {
      'type': 'type',
      'title': 'title',
      'description': 'description',
      'client id': 'clientId',
      'priority': 'priority',
      'frequency': 'frequency',
      'due date': 'dueDate',
      'preparer id': 'preparerId',
      'reviewer id': 'reviewerId',
      'submitter id': 'submitterId',
      'fee type': 'feeType',
      'fixed amount': 'fixedAmount',
      'billable rate': 'billableRate',
      'estimated hours': 'estimatedHours',
      'tags': 'tags'
    };

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());

      if (values.length !== headers.length) {
        errors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }

      const task: TaskImportData = {
        type: '',
        title: '',
        clientId: '',
        priority: 'medium',
        frequency: 'ONE_TIME',
      };

      let hasType = false;
      let hasTitle = false;
      let hasClientId = false;
      let hasFrequency = false;

      headers.forEach((header, index) => {
        const mappedField = headerMap[header];
        const value = values[index];

        if (!mappedField || !value) return;

        if (mappedField === 'type') {
          task.type = value;
          hasType = true;
        } else if (mappedField === 'title') {
          task.title = value;
          hasTitle = true;
        } else if (mappedField === 'description') {
          task.description = value;
        } else if (mappedField === 'clientId') {
          task.clientId = value;
          hasClientId = true;
        } else if (mappedField === 'priority') {
          const validPriorities = ['low', 'medium', 'high', 'urgent'];
          task.priority = validPriorities.includes(value.toLowerCase())
            ? (value.toLowerCase() as 'low' | 'medium' | 'high' | 'urgent')
            : 'medium';
        } else if (mappedField === 'frequency') {
          task.frequency = value;
          hasFrequency = true;
        } else if (mappedField === 'dueDate') {
          task.dueDate = value;
        } else if (mappedField === 'preparerId') {
          task.preparerId = value;
        } else if (mappedField === 'reviewerId') {
          task.reviewerId = value;
        } else if (mappedField === 'submitterId') {
          task.submitterId = value;
        } else if (mappedField === 'feeType') {
          task.feeType = value;
        } else if (mappedField === 'fixedAmount') {
          task.fixedAmount = parseFloat(value);
        } else if (mappedField === 'billableRate') {
          task.billableRate = parseFloat(value);
        } else if (mappedField === 'estimatedHours') {
          task.estimatedHours = parseFloat(value);
        } else if (mappedField === 'tags') {
          task.tags = value;
        }
      });

      if (!hasType) {
        errors.push(`Row ${i + 1}: Type is required`);
        continue;
      }

      if (!hasTitle) {
        errors.push(`Row ${i + 1}: Title is required`);
        continue;
      }

      if (!hasClientId) {
        errors.push(`Row ${i + 1}: Client ID is required`);
        continue;
      }

      if (!hasFrequency) {
        errors.push(`Row ${i + 1}: Frequency is required`);
        continue;
      }

      data.push(task);
    }

    return { data, errors };
  };

  const handleImport = () => {
    if (parsedData.length > 0 && selectedFile) {
      setStep('importing');
      onImport(parsedData, selectedFile);
    }
  };

  const downloadTemplate = async () => {
    try {
      // Download from API endpoint
      const response = await api.get('/tasks/import/template', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'task-import-template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template. Please try downloading from the server directly.');
      
      // Fallback: Use hardcoded template
      const template = [
        'Type,Title,Description,Client ID,Priority,Frequency,Due Date,Preparer ID,Reviewer ID,Submitter ID,Fee Type,Fixed Amount,Billable Rate,Estimated Hours,Tags',
        'BOOKKEEPING,"Monthly Reconciliation","Complete bank reconciliation",58a9c7d91f2e3c1a4b5c6d7e,high,MONTHLY,2025-12-31,68e4f5f43dad31217f31f22e,78f5e6g54ebe42228e32g33f,88g6f7h65fcf53339f43h44g,FIXED,1000,,8,"monthly,reconciliation"',
        'TAX_RETURN,"Annual Tax Return","Prepare 2024 tax return",59b9c7d91f2e3c1a4b5c6d7f,urgent,ONE_TIME,2025-12-15,68e4f5f43dad31217f31f22e,78f5e6g54ebe42228e32g33f,88g6f7h65fcf53339f43h44g,FIXED,2500,,16,"tax,annual"',
        'PAYROLL,"Monthly Payroll","Process monthly payroll",5aa9c7d91f2e3c1a4b5c6d7g,medium,MONTHLY,2025-11-30,69e4f5f43dad31217f31f22f,79f5e6g54ebe42228e32g33g,,HOURLY,,150,12,"payroll,monthly"'
      ].join('\n');

      const fallbackBlob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
      const fallbackLink = document.createElement('a');
      if (fallbackLink.download !== undefined) {
        const url = URL.createObjectURL(fallbackBlob);
        fallbackLink.setAttribute('href', url);
        fallbackLink.setAttribute('download', 'task-import-template.csv');
        fallbackLink.style.visibility = 'hidden';
        document.body.appendChild(fallbackLink);
        fallbackLink.click();
        document.body.removeChild(fallbackLink);
        URL.revokeObjectURL(url);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Import Tasks</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload CSV File</h3>
                <p className="text-gray-600 mb-4">
                  Upload a CSV file with task data. Make sure your file includes the required columns.
                </p>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload-tasks"
                />
                <label
                  htmlFor="csv-upload-tasks"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Click to upload CSV file</span>
                  <span className="text-xs text-gray-500">or drag and drop</span>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">CSV Format Requirements (15 Columns)</h4>
                    <ul className="text-sm text-blue-800 mt-1 list-disc list-inside">
                      <li>First row must contain column headers</li>
                      <li><strong>Required columns:</strong> Type, Title, Client ID, Frequency</li>
                      <li><strong>Optional columns:</strong> Description, Priority, Due Date, Preparer ID, Reviewer ID, Submitter ID, Fee Type, Fixed Amount, Billable Rate, Estimated Hours, Tags</li>
                      <li>Type values: BOOKKEEPING, TAX_RETURN, AUDIT, PAYROLL, CONSULTATION, OTHER</li>
                      <li>Priority: low, medium, high, or urgent (default: medium)</li>
                      <li>Frequency: ONE_TIME, WEEKLY, MONTHLY, QUARTERLY, ANNUALLY</li>
                      <li>Due Date format: YYYY-MM-DD</li>
                      <li>IDs (Client ID, Preparer ID, Reviewer ID, Submitter ID) must be valid ObjectIds</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Import Data</h3>
                <p className="text-gray-600">
                  Review the data below before importing. {parsedData.length} task(s) will be imported.
                </p>
              </div>

              {errors.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-900">Warnings</h4>
                      <ul className="text-sm text-yellow-800 mt-1 list-disc list-inside">
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Preparer ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reviewer ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Submitter ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {parsedData.map((task, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{task.type}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 truncate" title={task.title}>{task.title}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 truncate">{task.clientId}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{task.frequency}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{task.priority}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 truncate">{task.preparerId || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 truncate">{task.reviewerId || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 truncate">{task.submitterId || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{task.dueDate || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
                  disabled={parsedData.length === 0}
                >
                  Import {parsedData.length} Task(s)
                </button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Importing Tasks...</h3>
              <p className="text-gray-600">Please wait while we import your tasks.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

